import { Form, required, useGetIdentity } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { ReferenceArrayInput } from "@/components/admin/reference-array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput";
import { contactOptionText } from "../misc/ContactOption";
import { StarRatingInput } from "./StarRating";

/**
 * MeetingForm — los inputs reusables entre MeetingCreate y MeetingEdit.
 *
 * Para que React Hook Form maneje correctamente los campos del Form,
 * el wrapper (Create/Edit) debe envolver con `<Form>` y proveer defaults
 * apropiados (sales_id, occurred_at, language, source).
 *
 * Campos:
 *   - Empresa (required) → company_id
 *   - Oportunidad ligada (opcional, filtrada por empresa) → deal_id
 *   - Fecha y hora (datetime) → occurred_at
 *   - Canal (manual/whatsapp/telegram/email/web) → source
 *   - Título (opcional) → title
 *   - Asistentes (multi-select de contactos de la empresa) → attendee_ids
 *   - Temas / Acuerdos / Próximos pasos / Resumen / Transcripción (multilínea)
 *   - Audio (file input mono — sube a Storage, queda en audio_url)
 *   - Adjuntos (file input multi — fotos pizarra, transcripción, etc.)
 */
export function MeetingFormBody({
  fromCompanyId,
  fromDealId,
}: {
  fromCompanyId?: number | string;
  fromDealId?: number | string;
}) {
  return (
    <div className="flex flex-col gap-6">
      {/* Bloque 1 — Vinculación */}
      <section>
        <SectionTitle>Vinculación</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <ReferenceInput
            source="company_id"
            reference="companies"
            // si venimos desde una empresa, default ya está y no se muestra
          >
            <AutocompleteCompanyInput
              label="Empresa"
              validate={required("La empresa es obligatoria")}
            />
          </ReferenceInput>

          <DealForCompanyInput defaultDealId={fromDealId} />
        </div>

        <div className="mt-4">
          <AttendeesPicker fromCompanyId={fromCompanyId} />
        </div>
      </section>

      {/* Bloque 2 — Cuándo, dónde y cómo */}
      <section>
        <SectionTitle>Cuándo, dónde y cómo</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <DateTimeInput
            source="occurred_at"
            label="Fecha y hora"
            helperText="Cuándo ocurrió la junta"
            validate={required()}
          />
          <TextInput
            source="location"
            label="Lugar"
            helperText='Dónde fue. Ej: "Oficinas Cliente", "Zoom", "Restaurante"'
          />
          <SelectInput
            source="source"
            label="Canal de captura"
            helperText={false}
            choices={[
              { id: "manual", name: "Manual" },
              { id: "whatsapp", name: "WhatsApp" },
              { id: "telegram", name: "Telegram" },
              { id: "email", name: "Correo" },
              { id: "web", name: "Web (formulario)" },
            ]}
          />
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-5 items-end">
          <TextInput
            source="title"
            label="Título de la junta"
            helperText='Ej: "Visita técnica — revisión cancha 2"'
          />
          <div
            className="rounded-lg px-4 py-3"
            style={{
              background: "var(--musco-panel2)",
              border: "1px solid var(--musco-line)",
            }}
          >
            <StarRatingInput
              source="rating"
              label="Calificación de la junta"
              helperText="¿Qué tan productiva fue? 1 = mala, 5 = excelente"
            />
          </div>
        </div>
      </section>

      {/* Bloque 3 — Contenido (lo que se habló) */}
      <section>
        <SectionTitle>Contenido</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TextInput
            source="topics"
            label="Temas tratados"
            helperText="Un tema por línea — sale como bullets en la junta"
            multiline
            rows={5}
          />
          <TextInput
            source="agreements"
            label="Acuerdos"
            helperText="Lo que se decidió en esta reunión"
            multiline
            rows={5}
          />
          <TextInput
            source="next_steps"
            label="Próximos pasos"
            helperText="Un paso por línea — sale como tareas pendientes"
            multiline
            rows={5}
          />
          <TextInput
            source="summary"
            label="Resumen ejecutivo"
            helperText="3-5 líneas — para revisar rápido más adelante"
            multiline
            rows={5}
          />
        </div>
        <div className="mt-4">
          <TextInput
            source="transcript"
            label="Transcripción completa"
            helperText="Pega el texto completo de la transcripción si la tienes. Formato sugerido: '00:01:23 — Speaker: texto'"
            multiline
            rows={8}
          />
        </div>
      </section>

    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="text-sm font-semibold mb-3 uppercase tracking-wider"
      style={{ color: "var(--musco-muted)" }}
    >
      {children}
    </h2>
  );
}

/**
 * AttendeesPicker — multi-select de contactos, filtrado por la empresa
 * actualmente seleccionada en el form. Si no hay empresa seleccionada
 * todavía, muestra todos los contactos.
 */
function AttendeesPicker({
  fromCompanyId,
}: {
  fromCompanyId?: number | string;
}) {
  const companyId = useWatch<any>({ name: "company_id" }) ?? fromCompanyId;
  return (
    <ReferenceArrayInput
      source="attendee_ids"
      reference="contacts_summary"
      filter={companyId ? { company_id: companyId } : undefined}
      perPage={50}
    >
      <AutocompleteArrayInput
        label="Asistentes (contactos)"
        optionText={contactOptionText}
        helperText={
          companyId
            ? "Solo se muestran contactos de la empresa seleccionada"
            : "Selecciona primero una empresa para filtrar los contactos"
        }
      />
    </ReferenceArrayInput>
  );
}

/**
 * DealForCompanyInput — autocomplete de oportunidades filtrado por la
 * company_id actualmente seleccionada. Si la empresa cambia, se borra
 * la oportunidad seleccionada (que ya no pertenece a esa empresa).
 */
function DealForCompanyInput({
  defaultDealId,
}: {
  defaultDealId?: number | string;
}) {
  const companyId = useWatch<any>({ name: "company_id" });
  const { setValue } = useFormContext();
  const currentDealId = useWatch<any>({ name: "deal_id" });

  // Si la empresa cambia y la oportunidad seleccionada ya no le pertenece,
  // limpiarla. (No podemos saberlo sin fetch — confiamos en el filtro del
  // autocomplete y la limpiamos cuando company_id cambia desde un valor.)
  return (
    <ReferenceInput
      source="deal_id"
      reference="deals"
      filter={companyId ? { company_id: companyId } : undefined}
      perPage={50}
    >
      <AutocompleteInput
        label="Oportunidad ligada (opcional)"
        optionText="name"
        helperText={
          companyId
            ? "Vincular esta junta a una de las oportunidades de la empresa"
            : "Primero selecciona una empresa"
        }
        defaultValue={defaultDealId}
        onChange={(v) => setValue("deal_id", v)}
      />
    </ReferenceInput>
  );
}

/** Form wrapper que aplica defaults sensatos para Create. */
export function MeetingFormCreate({
  children,
  fromCompanyId,
  fromDealId,
}: {
  children: React.ReactNode;
  fromCompanyId?: number | string;
  fromDealId?: number | string;
}) {
  const { identity } = useGetIdentity();
  return (
    <Form
      defaultValues={{
        sales_id: identity?.id,
        source: "manual",
        language: "es",
        occurred_at: new Date().toISOString(),
        company_id: fromCompanyId,
        deal_id: fromDealId,
        attendee_ids: [],
      }}
    >
      {children}
    </Form>
  );
}
